import React, { useState, useRef } from "react";
import { parseSRT } from "../utils/srtParser";
import { SubtitleEntry } from "../types";
import "./SrtUploader.css";

interface SrtUploaderProps {
  onSrtLoaded: (subtitles: SubtitleEntry[]) => void;
}

const SrtUploader: React.FC<SrtUploaderProps> = ({ onSrtLoaded }) => {
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const processSrtFile = (file: File) => {
    setIsLoading(true);
    setError(null);

    // Validate file type
    if (!file.name.endsWith(".srt")) {
      setError("Please upload a valid .srt file");
      setIsLoading(false);
      return;
    }

    // Store the file name for display
    setFileName(file.name);

    // Read the file content
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const fileContent = event.target?.result as string;
        console.log("Uploaded SRT: ", fileContent);
        const subtitles = parseSRT(fileContent);

        if (subtitles.length === 0) {
          throw new Error("No valid subtitles found in the file");
        }

        onSrtLoaded(subtitles);
        setIsLoading(false);
      } catch (err) {
        setError(
          "Failed to parse SRT file. Please ensure it's a valid SRT format."
        );
        setIsLoading(false);
      }
    };

    reader.onerror = () => {
      setError("Error reading the file. Please try again.");
      setIsLoading(false);
    };

    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processSrtFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (e.target.files && e.target.files.length > 0) {
      processSrtFile(e.target.files[0]);
    }
  };

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  return (
    <div className="srt-uploader-container">
      <h2>Upload Your Own SRT File</h2>
      <p className="legal-disclaimer">
        Please ensure you have the legal right to use any SRT files you upload.
        Files are processed in your browser and are not stored on our servers.
      </p>

      <div
        className={`upload-area ${dragActive ? "drag-active" : ""}`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".srt"
          onChange={handleChange}
          className="file-input"
        />

        <div className="upload-content">
          <div className="upload-icon">📄</div>
          <p>Drag and drop your SRT file here, or</p>
          <button className="upload-btn" onClick={handleButtonClick}>
            Choose File
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="loading-indicator">
          <p>Processing SRT file...</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {fileName && !error && !isLoading && (
        <div className="success-message">
          <p>
            ✅ Successfully loaded: <strong>{fileName}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default SrtUploader;
